// @ts-check
import { applyAssetPlanStrokeFactor } from './asset_plan_stroke.mjs';
import { reservePlanCaptureAsset } from '../render/plan_capture_context.mjs';

import { calculateImportedSvgContentBounds, parseImportedSvgLengthMm } from './imported_svg_placement_metadata.mjs';

import { S } from '../app/app_state.js?v=20260817c';
import {
  getCanvasSize,
  screenToWorld,
  svgEl,
  worldToScreen
} from '../render/plan_renderer.js?v=20260904f';
import { requestFrame } from '../render/render_scheduler.js?v=20260706e';
import {
  buildImportedSvgRenderModel,
  contentBoundsFromRenderModel
} from './imported_svg_render_model.js';
import {
  DEFAULT_SVG_VIEWPORT_VIRTUALIZATION_CONFIG,
  createImportedSvgPlacement,
  expandAabb,
  importedSvgPlacementWorldAabb,
  importedSvgWorldAabbToLocalAabb,
  sameOrderedIds,
  selectSvgVisibleUnitIds,
  svgViewportVirtualizationEnabled,
  worldViewportAabb
} from './imported_svg_virtualization.mjs';
import {
  currentImportedSvgRenderPass,
  importedSvgDiagnosticsEnabled,
  importedSvgRenderForcesFullDom,
  importedSvgRuntimeController,
  recordImportedSvgAssetDiagnostics,
  registerImportedSvgRuntimeController,
  removeImportedSvgRuntimeController,
  touchImportedSvgRuntimeController
} from './imported_svg_viewport_runtime.js';

let importedSvgInstanceSeq = 0;

export async function loadImportedSvgAsset(path, options = {}) {
  const svgPath = String(path || '').trim();
  if (!svgPath) throw new Error('SVG-Pfad fehlt');
  const response = await fetch(svgPath, { cache: options.cache || 'no-store' });
  if (!response.ok) throw new Error(`SVG konnte nicht geladen werden (${response.status})`);
  return parseImportedSvgAsset(await response.text(), svgPath, options);
}

/** Parse already verified bytes without a second network read. */
export function parseImportedSvgAsset(text, svgPath, options = {}) {
  const parseStartedAt = importedSvgNowMs();
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const parsingMs = importedSvgNowMs() - parseStartedAt;
  if (doc.querySelector('parsererror')) throw new Error('SVG Parserfehler');
  const root = doc.documentElement;
  if (!root || root.tagName.toLowerCase() !== 'svg') throw new Error('Ungueltige SVG-Datei');
  const legacyBounds = calculateImportedSvgContentBounds(root);
  applyAssetPlanStrokeFactor(root, options.planStrokeWidthFactor);
  // A disabled feature flag is a true rollback path: it does not pay model,
  // measurement or index cost while loading and leaves the source untouched.
  const renderModel = svgViewportVirtualizationEnabled()
    ? buildImportedSvgRenderModel(root, {
        ...DEFAULT_SVG_VIEWPORT_VIRTUALIZATION_CONFIG,
        rewriteIds: prefixImportedSvgIds,
        development: importedSvgDevelopmentRuntime()
      })
    : null;
  const asset = {
    root,
    sourcePath: svgPath,
    // Preserve the established placement/scaling bounds whenever the legacy
    // parser can determine them. The measured model bounds deliberately add
    // conservative stroke padding for culling and must not subtly resize an
    // already stored asset.
    contentBounds: legacyBounds || contentBoundsFromRenderModel(renderModel, null),
    widthMm: parseImportedSvgLengthMm(root.getAttribute('width')),
    depthMm: parseImportedSvgLengthMm(root.getAttribute('height')),
    renderModel,
    performance: {
      parsingMs,
      boundsMs: Number(renderModel?.boundsMs) || 0,
      indexMs: Number(renderModel?.indexMs) || 0,
      loadedAt: importedSvgNowMs(),
      firstVisibleMs: null,
      firstVisiblePending: false
    }
  };
  recordImportedSvgAssetDiagnostics(svgPath, importedSvgAssetDiagnosticValues(asset));
  return asset;
}

export {
  createImportedSvgIdRewriter,
  prefixImportedSvgIds
};

export function drawImportedSvgAsset(parent, asset, options = {}) {
  if (!parent || !asset?.root) return false;
  reservePlanCaptureAsset(asset.root);
  const model = asset.renderModel;
  const pass = currentImportedSvgRenderPass();
  const scope = String(pass?.isolated ? pass.scope : options.renderScope || pass?.scope || 'plan');
  const instanceKey = String(options.instanceKey || '').trim();
  const runtimeKey = instanceKey ? `${scope}:${instanceKey}` : '';
  const virtualizationEnabled = svgViewportVirtualizationEnabled();
  // Product renderers may apply additional transforms to the parent. Reuse
  // their SVG trees without culling against an incomplete world placement.
  const viewportCulling = options.viewportCulling !== false;
  const mayVirtualizeInternally = virtualizationEnabled
    && Boolean(runtimeKey)
    && viewportCulling
    && model?.virtualizable === true;
  const mayReuseWholeAsset = virtualizationEnabled
    && Boolean(runtimeKey)
    && (!viewportCulling || model?.fallbackReason === 'below-threshold');
  if (!mayVirtualizeInternally && !mayReuseWholeAsset) {
    if (runtimeKey) removeImportedSvgRuntimeController(runtimeKey);
    return drawImportedSvgAssetFull(parent, asset, options);
  }

  let controller = importedSvgRuntimeController(runtimeKey);
  const appearanceKey = importedSvgAppearanceKey(options);
  const controllerKind = mayVirtualizeInternally ? 'internal' : 'asset';
  if (controller && (controller.asset !== asset
      || controller.appearanceKey !== appearanceKey
      || controller.kind !== controllerKind)) {
    removeImportedSvgRuntimeController(runtimeKey);
    controller = null;
  }
  if (!controller) {
    // Class is declared below the public drawing entry point to keep the
    // legacy full-render path readable alongside it.
    if (mayVirtualizeInternally) {
      // eslint-disable-next-line no-use-before-define
      controller = new ImportedSvgViewportController(runtimeKey, asset, options, scope);
    } else {
      // eslint-disable-next-line no-use-before-define
      controller = new WholeImportedSvgAssetController(runtimeKey, asset, options, scope);
    }
    registerImportedSvgRuntimeController(runtimeKey, controller, { scope });
  } else {
    touchImportedSvgRuntimeController(runtimeKey);
  }
  controller.attach(parent, options);
  const viewport = viewportCulling ? importedSvgCurrentWorldViewport() : null;
  controller.updateViewport({
    scope,
    worldBounds: viewport,
    reason: importedSvgRenderForcesFullDom() ? 'full-output' : 'render'
  }, { forceFull: importedSvgRenderForcesFullDom() });
  return true;
}

function drawImportedSvgAssetFull(parent, asset, options = {}) {
  const placement = createImportedSvgPlacement(asset, options);
  const transform = importedSvgScreenTransform(placement);
  const layer = svgEl('g', {
    class: options.className || 'imported-svg-asset',
    transform,
    opacity: Number.isFinite(Number(options.opacity)) ? Number(options.opacity) : 1,
    'pointer-events': options.pointerEvents || 'none',
    ...(options.objectAssetId ? { 'data-object-asset-id': options.objectAssetId } : {})
  }, parent);
  const idRewriter = createImportedSvgIdRewriter(
    asset.root,
    `${options.idPrefix || 'imported-svg'}-${importedSvgInstanceSeq += 1}`
  );
  const root = importedSvgCloneForInstance(asset.root, true, idRewriter, {
    fillColor: options.fillColor,
    strokeColor: options.strokeColor
  });
  // root is already the one deep clone this draw needs (it's detached, never
  // inserted anywhere itself) -- re-parent its already-cloned children
  // directly instead of cloning them a second time. This ran twice per
  // instance per redraw before (once for the whole tree, once per child),
  // which mattered a lot here: drawImportedSvgAsset clones fresh on every
  // single 2D redraw for every placed instance (every click/drag triggers a
  // full render2D pass), so halving the clone cost is a real win at scale.
  Array.from(root.childNodes || []).forEach(node => layer.appendChild(node));
  const viewport = importedSvgCurrentWorldViewport();
  const worldBounds = importedSvgPlacementWorldAabb(placement);
  if (!viewport || !worldBounds || aabbIntersectsImportedSvg(viewport, worldBounds)) {
    importedSvgMarkFirstVisible(asset, () => layer.isConnected === true);
  }
  return true;
}

function importedSvgNowMs() {
  return Number(globalThis.performance?.now?.()) || Date.now();
}

function importedSvgDevelopmentRuntime() {
  if (import.meta.env?.DEV === true) return true;
  const host = String(globalThis?.location?.hostname || '').toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
}

function importedSvgAssetDiagnosticValues(asset) {
  return {
    totalDomNodes: Number(asset?.renderModel?.totalDomNodes) || 0,
    drawingElements: Number(asset?.renderModel?.drawingElements) || 0,
    renderUnits: Number(asset?.renderModel?.units?.length) || 0,
    virtualizable: asset?.renderModel?.virtualizable === true,
    fallbackReason: asset?.renderModel?.fallbackReason || '',
    parsingMs: Number(asset?.performance?.parsingMs) || 0,
    boundsMs: Number(asset?.performance?.boundsMs) || 0,
    indexMs: Number(asset?.performance?.indexMs) || 0,
    firstVisibleMs: Number(asset?.performance?.firstVisibleMs) || 0
  };
}

function importedSvgMarkFirstVisible(asset, isStillVisible = null) {
  const performanceState = asset?.performance;
  if (performanceState
      && !Number.isFinite(performanceState.firstVisibleMs)
      && performanceState.firstVisiblePending !== true
      && importedSvgDiagnosticsEnabled()) {
    performanceState.firstVisiblePending = true;
    requestFrame(() => {
      requestFrame(() => {
        if (typeof isStillVisible === 'function' && !isStillVisible()) {
          performanceState.firstVisiblePending = false;
          return;
        }
        performanceState.firstVisibleMs = importedSvgNowMs()
          - (Number(performanceState.loadedAt) || importedSvgNowMs());
        performanceState.firstVisiblePending = false;
        recordImportedSvgAssetDiagnostics(
          asset?.sourcePath || asset?.renderModel?.id,
          importedSvgAssetDiagnosticValues(asset)
        );
      });
    });
  }
  recordImportedSvgAssetDiagnostics(asset?.sourcePath || asset?.renderModel?.id, importedSvgAssetDiagnosticValues(asset));
}

function importedSvgCurrentWorldViewport() {
  try {
    const { w, h } = getCanvasSize();
    return worldViewportAabb(screenToWorld, w, h);
  } catch (_error) {
    return null;
  }
}

function importedSvgScreenTransform(placement) {
  const topLeft = worldToScreen(placement.originX, placement.originZ);
  const screenCenter = worldToScreen(placement.rotationCenter.x, placement.rotationCenter.z);
  const cameraScale = Number(S.camera2D?.scale) || 1;
  const transform = [
    `rotate(${placement.rotationDeg} ${screenCenter.x} ${screenCenter.y})`,
    `translate(${topLeft.x} ${topLeft.y})`
  ];
  const scaleX = cameraScale * placement.scaleX;
  const scaleZ = cameraScale * placement.scaleZ;
  if (placement.mirrorX || placement.mirrorZ) {
    transform.push(`translate(${placement.mirrorX ? placement.renderWidth * cameraScale : 0} ${placement.mirrorZ ? placement.renderDepth * cameraScale : 0})`);
    transform.push(`scale(${placement.mirrorX ? -scaleX : scaleX} ${placement.mirrorZ ? -scaleZ : scaleZ})`);
  } else {
    transform.push(`scale(${scaleX} ${scaleZ})`);
  }
  transform.push(`translate(${-Number(placement.sourceBounds.minX || 0)} ${-Number(placement.sourceBounds.minZ || 0)})`);
  return transform.join(' ');
}

function importedSvgAppearanceKey(options) {
  return JSON.stringify([
    options.idPrefix || '',
    options.fillColor || '',
    options.strokeColor || ''
  ]);
}

function sanitizeImportedSvgRuntimeKey(value) {
  // Readable prefix only: each controller also appends a unique sequence,
  // because sanitizing and truncating distinct keys can produce the same text.
  return String(value || 'asset').replace(/[^a-zA-Z0-9_-]+/g, '-').slice(-100);
}

class ImportedSvgViewportController {
  constructor(key, asset, options, scope) {
    this.key = key;
    this.asset = asset;
    this.model = asset.renderModel;
    this.scope = scope;
    this.kind = 'internal';
    this.appearanceKey = importedSvgAppearanceKey(options);
    this.colorOptions = {
      fillColor: options.fillColor,
      strokeColor: options.strokeColor
    };
    this.totalUnits = this.model.units.length;
    this.mountedIds = [];
    this.nodesById = new Map();
    this.parentNodes = new Map();
    this.nodeSiblingOrder = new WeakMap();
    this.pinnedIds = new Set();
    this.placement = createImportedSvgPlacement(asset, options);
    this.idRewriter = createImportedSvgIdRewriter(
      asset.root,
      `${options.idPrefix || 'imported-svg'}-${sanitizeImportedSvgRuntimeKey(key)}-${importedSvgInstanceSeq += 1}`
    );
    this.host = svgEl('g', {});
    this.parentNodes.set('root', this.host);
    this.#buildSkeleton(this.model.descriptors, this.host);
  }

  attach(parent, options) {
    this.placement = createImportedSvgPlacement(this.asset, options);
    this.host.setAttribute('class', options.className || 'imported-svg-asset');
    this.host.setAttribute('transform', importedSvgScreenTransform(this.placement));
    this.host.setAttribute('opacity', Number.isFinite(Number(options.opacity)) ? Number(options.opacity) : 1);
    this.host.setAttribute('pointer-events', options.pointerEvents || 'none');
    if (options.objectAssetId) this.host.setAttribute('data-object-asset-id', options.objectAssetId);
    else this.host.removeAttribute('data-object-asset-id');
    if (this.host.parentNode !== parent) parent.appendChild(this.host);
  }

  updateViewport(state = {}, options = {}) {
    const forceFull = options.forceFull === true;
    let desiredIds;
    if (forceFull || !state.worldBounds) {
      desiredIds = this.model.index.ordered(this.model.units.map(unit => unit.id));
    } else {
      const strokeMargin = importedSvgNonScalingStrokeWorldMargin(this.model);
      const enterWorld = expandAabb(
        state.worldBounds,
        DEFAULT_SVG_VIEWPORT_VIRTUALIZATION_CONFIG.overscanRatio,
        strokeMargin
      );
      const exitWorld = expandAabb(
        state.worldBounds,
        DEFAULT_SVG_VIEWPORT_VIRTUALIZATION_CONFIG.overscanRatio
          + DEFAULT_SVG_VIEWPORT_VIRTUALIZATION_CONFIG.hysteresisRatio,
        strokeMargin
      );
      const assetBounds = importedSvgPlacementWorldAabb(this.placement, this.model.contentBounds);
      const coarseViewport = this.mountedIds.length ? exitWorld : enterWorld;
      if (this.model.assetCullable !== false
          && assetBounds
          && !aabbIntersectsImportedSvg(assetBounds, coarseViewport)) {
        desiredIds = [];
      } else {
        const enterLocal = importedSvgWorldAabbToLocalAabb(enterWorld, this.placement);
        const exitLocal = importedSvgWorldAabbToLocalAabb(exitWorld, this.placement);
        desiredIds = selectSvgVisibleUnitIds(
          this.model.index,
          enterLocal,
          exitLocal,
          this.mountedIds,
          this.pinnedIds
        );
      }
    }
    const result = sameOrderedIds(desiredIds, this.mountedIds)
      ? { mounts: 0, unmounts: 0, mountedUnits: this.mountedIds.length, reconciled: false }
      : this.#reconcile(desiredIds);
    if (this.mountedIds.length) {
      importedSvgMarkFirstVisible(this.asset, () => (
        this.mountedIds.length > 0 && this.host?.isConnected === true
      ));
    }
    return result;
  }

  dispose() {
    this.host?.remove?.();
    this.nodesById.clear();
    this.parentNodes.clear();
    this.mountedIds = [];
  }

  #buildSkeleton(descriptors, parent) {
    for (const descriptor of descriptors || []) {
      if (descriptor.type === 'unit') continue;
      const deep = descriptor.type === 'support';
      const clone = importedSvgCloneForInstance(
        descriptor.sourceElement,
        deep,
        this.idRewriter,
        this.colorOptions
      );
      this.nodeSiblingOrder.set(clone, descriptor.siblingIndex);
      parent.appendChild(clone);
      if (descriptor.type === 'shell') {
        this.parentNodes.set(descriptor.key, clone);
        this.#buildSkeleton(descriptor.children, clone);
      }
    }
  }

  #reconcile(desiredIds) {
    const desired = new Set(desiredIds);
    let unmounts = 0;
    let mounts = 0;
    for (const [id, node] of this.nodesById) {
      if (desired.has(id)) continue;
      node.remove?.();
      this.nodesById.delete(id);
      unmounts += 1;
    }

    const pendingByParent = new Map();
    for (const id of desiredIds) {
      if (this.nodesById.has(id)) continue;
      const unit = this.model.index.unitsById.get(id);
      const parent = this.parentNodes.get(unit?.parentKey || 'root') || this.host;
      if (!unit?.sourceElement || !parent) continue;
      let pending = pendingByParent.get(parent);
      if (!pending) {
        pending = [];
        pendingByParent.set(parent, pending);
      }
      pending.push({ id, unit });
    }

    for (const [parent, pending] of pendingByParent) {
      pending.sort((left, right) => left.unit.siblingIndex - right.unit.siblingIndex);
      const existingChildren = Array.from(parent.children || []);
      let existingIndex = 0;
      for (const { id, unit } of pending) {
        while (existingIndex < existingChildren.length
            && Number(this.nodeSiblingOrder.get(existingChildren[existingIndex])) < Number(unit.siblingIndex)) {
          existingIndex += 1;
        }
        const before = existingChildren[existingIndex] || null;
        const clone = importedSvgCloneForInstance(
          unit.sourceElement,
          true,
          this.idRewriter,
          this.colorOptions
        );
        this.nodeSiblingOrder.set(clone, unit.siblingIndex);
        parent.insertBefore(clone, before);
        this.nodesById.set(id, clone);
        mounts += 1;
      }
    }
    this.mountedIds = desiredIds.filter(id => this.nodesById.has(id));
    return { mounts, unmounts, mountedUnits: this.mountedIds.length, reconciled: true };
  }
}

class WholeImportedSvgAssetController {
  constructor(key, asset, options, scope) {
    this.key = key;
    this.asset = asset;
    this.model = asset.renderModel;
    this.scope = scope;
    this.kind = 'asset';
    this.appearanceKey = importedSvgAppearanceKey(options);
    this.colorOptions = {
      fillColor: options.fillColor,
      strokeColor: options.strokeColor
    };
    this.totalUnits = 1;
    this.mountedIds = [];
    this.nodesById = new Map();
    this.placement = createImportedSvgPlacement(asset, options);
    this.idRewriter = createImportedSvgIdRewriter(
      asset.root,
      `${options.idPrefix || 'imported-svg'}-${sanitizeImportedSvgRuntimeKey(key)}-${importedSvgInstanceSeq += 1}`
    );
    this.host = svgEl('g', {});
  }

  attach(parent, options) {
    this.viewportCulling = options.viewportCulling !== false;
    this.placement = createImportedSvgPlacement(this.asset, options);
    this.host.setAttribute('class', options.className || 'imported-svg-asset');
    this.host.setAttribute('transform', importedSvgScreenTransform(this.placement));
    this.host.setAttribute('opacity', Number.isFinite(Number(options.opacity)) ? Number(options.opacity) : 1);
    this.host.setAttribute('pointer-events', options.pointerEvents || 'none');
    if (options.objectAssetId) this.host.setAttribute('data-object-asset-id', options.objectAssetId);
    else this.host.removeAttribute('data-object-asset-id');
    if (this.host.parentNode !== parent) parent.appendChild(this.host);
  }

  updateViewport(state = {}, options = {}) {
    const forceFull = options.forceFull === true || this.viewportCulling === false;
    let shouldMount = forceFull || !state.worldBounds;
    if (!shouldMount) {
      const enterWorld = expandAabb(
        state.worldBounds,
        DEFAULT_SVG_VIEWPORT_VIRTUALIZATION_CONFIG.overscanRatio
      );
      const exitWorld = expandAabb(
        state.worldBounds,
        DEFAULT_SVG_VIEWPORT_VIRTUALIZATION_CONFIG.overscanRatio
          + DEFAULT_SVG_VIEWPORT_VIRTUALIZATION_CONFIG.hysteresisRatio
      );
      const assetBounds = importedSvgPlacementWorldAabb(this.placement);
      shouldMount = !assetBounds || aabbIntersectsImportedSvg(
        assetBounds,
        this.mountedIds.length ? exitWorld : enterWorld
      );
    }
    const wasMounted = this.mountedIds.length > 0;
    if (shouldMount === wasMounted) {
      if (shouldMount) this.#markFirstVisible();
      return { mounts: 0, unmounts: 0, mountedUnits: wasMounted ? 1 : 0, reconciled: false };
    }
    if (shouldMount) {
      const cloneRoot = importedSvgCloneForInstance(
        this.asset.root,
        true,
        this.idRewriter,
        this.colorOptions
      );
      const children = Array.from(cloneRoot.childNodes || []);
      for (const child of children) this.host.appendChild(child);
      this.mountedIds = ['full'];
      this.nodesById.set('full', children[0] || this.host);
      this.#markFirstVisible();
      return { mounts: 1, unmounts: 0, mountedUnits: 1, reconciled: true };
    }
    this.host.replaceChildren();
    this.nodesById.clear();
    this.mountedIds = [];
    return { mounts: 0, unmounts: 1, mountedUnits: 0, reconciled: true };
  }

  dispose() {
    this.host?.remove?.();
    this.nodesById.clear();
    this.mountedIds = [];
  }

  #markFirstVisible() {
    importedSvgMarkFirstVisible(this.asset, () => (
      this.mountedIds.length > 0 && this.host?.isConnected === true
    ));
  }
}

function importedSvgNonScalingStrokeWorldMargin(model) {
  const screenExtent = Math.max(0, Number(model?.maxNonScalingStrokeExtent) || 0);
  if (!screenExtent) return 0;
  return screenExtent / Math.max(1e-9, Math.abs(Number(S.camera2D?.scale) || 1));
}

function aabbIntersectsImportedSvg(left, right) {
  return left.minX <= right.maxX
    && left.maxX >= right.minX
    && left.minZ <= right.maxZ
    && left.maxZ >= right.minZ;
}

const IMPORTED_SVG_PAINT_REFERENCE_ATTRIBUTES = ['fill', 'stroke'];
const IMPORTED_SVG_EFFECT_REFERENCE_ATTRIBUTES = [
  'filter', 'clip-path', 'mask',
  'marker-start', 'marker-mid', 'marker-end'
];
const IMPORTED_SVG_HREF_REFERENCE_ATTRIBUTES = ['href', 'xlink:href'];
const IMPORTED_SVG_URL_REFERENCE_PATTERN = /url\(\s*(?:(["'])\s*#([^"']*?)\s*\1|#([^\s)"']+))\s*\)/gi;
const IMPORTED_SVG_EXACT_HASH_REFERENCE_PATTERN = /^\s*#([^\s]+)\s*$/;
const IMPORTED_SVG_CSS_GROUPING_AT_RULE_PATTERN = /^@(media|supports|layer|container|document|scope)\b/i;
const importedSvgIdSourceCache = new WeakMap();

function compiledImportedSvgIdSource(root) {
  if (!root || (typeof root !== 'object' && typeof root !== 'function')) {
    return { ids: [], selectorPatternSource: '' };
  }
  const cached = importedSvgIdSourceCache.get(root);
  if (cached) return cached;
  const ids = [];
  const seen = new Set();
  const idNodes = [root, ...Array.from(root.querySelectorAll?.('[id]') || [])];
  for (const node of idNodes) {
    const id = String(node.getAttribute?.('id') || '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  const selectorIds = ids
    .filter(id => /^-?(?:[a-zA-Z_][a-zA-Z0-9_-]*)$/.test(id))
    .sort((left, right) => right.length - left.length)
    .map(escapeImportedSvgIdForRegExp);
  const compiled = {
    ids,
    selectorPatternSource: selectorIds.length
      ? `#(${selectorIds.join('|')})(?![a-zA-Z0-9_-])`
      : ''
  };
  importedSvgIdSourceCache.set(root, compiled);
  return compiled;
}

function createImportedSvgIdRewriter(sourceRoot, prefix) {
  const source = compiledImportedSvgIdSource(sourceRoot);
  const idMap = new Map(source.ids.map(id => [id, `${prefix}-${id}`]));
  const selectorPattern = source.selectorPatternSource
    ? new RegExp(source.selectorPatternSource, 'y')
    : null;

  const rewriteUrlReferences = value => {
    const input = String(value || '');
    IMPORTED_SVG_URL_REFERENCE_PATTERN.lastIndex = 0;
    return input.replace(
      IMPORTED_SVG_URL_REFERENCE_PATTERN,
      (match, quote, quotedId, unquotedId) => {
        const oldId = String(quotedId ?? unquotedId ?? '').trim();
        const newId = idMap.get(oldId);
        if (!newId) return match;
        return quote ? `url(${quote}#${newId}${quote})` : `url(#${newId})`;
      }
    );
  };

  const rewriteExactHashReference = value => {
    const input = String(value || '');
    const match = IMPORTED_SVG_EXACT_HASH_REFERENCE_PATTERN.exec(input);
    const newId = match ? idMap.get(match[1]) : null;
    return newId ? `#${newId}` : input;
  };

  const rewriteSelectorIds = value => rewriteImportedSvgCssSelectorIds(
    String(value || ''),
    selectorPattern,
    idMap
  );

  const rewriteStyleText = value => rewriteImportedSvgCssRuleList(
    rewriteUrlReferences(value),
    rewriteSelectorIds
  );

  const rewriteTree = root => {
    if (!root || !idMap.size) return root;
    const nodes = [root, ...Array.from(root.querySelectorAll?.('*') || [])];
    for (const node of nodes) {
      const id = node.getAttribute?.('id');
      if (idMap.has(id)) node.setAttribute('id', idMap.get(id));

      for (const attribute of IMPORTED_SVG_PAINT_REFERENCE_ATTRIBUTES) {
        rewriteImportedSvgNodeAttribute(node, attribute, rewriteUrlReferences);
      }
      for (const attribute of IMPORTED_SVG_EFFECT_REFERENCE_ATTRIBUTES) {
        rewriteImportedSvgNodeAttribute(node, attribute, value => (
          rewriteExactHashReference(rewriteUrlReferences(value))
        ));
      }
      for (const attribute of IMPORTED_SVG_HREF_REFERENCE_ATTRIBUTES) {
        rewriteImportedSvgNodeAttribute(node, attribute, rewriteExactHashReference);
      }

      rewriteImportedSvgNodeAttribute(node, 'style', rewriteUrlReferences);
      if (String(node.localName || node.tagName || '').toLowerCase() === 'style' && node.textContent) {
        node.textContent = rewriteStyleText(node.textContent);
      }
    }
    return root;
  };

  return Object.freeze({ rewriteTree });
}

function rewriteImportedSvgNodeAttribute(node, attribute, rewrite) {
  const value = node.getAttribute?.(attribute);
  if (!value) return;
  const next = rewrite(value);
  if (next !== value) node.setAttribute(attribute, next);
}

function rewriteImportedSvgCssSelectorIds(selector, selectorPattern, idMap) {
  if (!selectorPattern || !selector || !idMap.size) return selector;
  let result = '';
  let index = 0;
  let squareDepth = 0;
  while (index < selector.length) {
    const char = selector[index];
    if (char === '/' && selector[index + 1] === '*') {
      const end = selector.indexOf('*/', index + 2);
      const next = end < 0 ? selector.length : end + 2;
      result += selector.slice(index, next);
      index = next;
      continue;
    }
    if (char === '"' || char === "'") {
      const next = importedSvgCssQuotedEnd(selector, index, char);
      result += selector.slice(index, next);
      index = next;
      continue;
    }
    if (char === '[') squareDepth += 1;
    else if (char === ']' && squareDepth > 0) squareDepth -= 1;
    if (char === '#' && squareDepth === 0 && selector[index - 1] !== '\\') {
      selectorPattern.lastIndex = index;
      const match = selectorPattern.exec(selector);
      const newId = match ? idMap.get(match[1]) : null;
      if (match && newId) {
        result += `#${newId}`;
        index = selectorPattern.lastIndex;
        continue;
      }
    }
    result += char;
    index += 1;
  }
  return result;
}

function importedSvgCssQuotedEnd(value, start, quote) {
  let index = start + 1;
  while (index < value.length) {
    if (value[index] === '\\') {
      index += 2;
      continue;
    }
    index += 1;
    if (value[index - 1] === quote) break;
  }
  return Math.min(index, value.length);
}

function importedSvgCssRuleDelimiter(value, start) {
  let parentheses = 0;
  let brackets = 0;
  for (let index = start; index < value.length; index += 1) {
    const char = value[index];
    if (char === '/' && value[index + 1] === '*') {
      const end = value.indexOf('*/', index + 2);
      if (end < 0) return { index: value.length, char: '' };
      index = end + 1;
      continue;
    }
    if (char === '"' || char === "'") {
      index = importedSvgCssQuotedEnd(value, index, char) - 1;
      continue;
    }
    if (char === '(') parentheses += 1;
    else if (char === ')' && parentheses > 0) parentheses -= 1;
    else if (char === '[') brackets += 1;
    else if (char === ']' && brackets > 0) brackets -= 1;
    else if (parentheses === 0 && brackets === 0 && (char === '{' || char === ';')) {
      return { index, char };
    }
  }
  return { index: value.length, char: '' };
}

function importedSvgCssClosingBrace(value, openingIndex) {
  let depth = 1;
  for (let index = openingIndex + 1; index < value.length; index += 1) {
    const char = value[index];
    if (char === '/' && value[index + 1] === '*') {
      const end = value.indexOf('*/', index + 2);
      if (end < 0) return value.length;
      index = end + 1;
      continue;
    }
    if (char === '"' || char === "'") {
      index = importedSvgCssQuotedEnd(value, index, char) - 1;
      continue;
    }
    if (char === '{') depth += 1;
    else if (char === '}') {
      depth -= 1;
      if (depth === 0) return index;
    }
  }
  return value.length;
}

function importedSvgCssPreludeKind(prelude) {
  const normalized = String(prelude || '').replace(/\/\*[\s\S]*?\*\//g, '').trim();
  if (!normalized.startsWith('@')) return 'selector';
  return IMPORTED_SVG_CSS_GROUPING_AT_RULE_PATTERN.test(normalized) ? 'grouping' : 'at-rule';
}

function rewriteImportedSvgCssRuleList(value, rewriteSelectorIds) {
  const css = String(value || '');
  let result = '';
  let cursor = 0;
  while (cursor < css.length) {
    const delimiter = importedSvgCssRuleDelimiter(css, cursor);
    if (!delimiter.char) {
      result += css.slice(cursor);
      break;
    }
    if (delimiter.char === ';') {
      result += css.slice(cursor, delimiter.index + 1);
      cursor = delimiter.index + 1;
      continue;
    }
    const prelude = css.slice(cursor, delimiter.index);
    const closingIndex = importedSvgCssClosingBrace(css, delimiter.index);
    if (closingIndex >= css.length) {
      result += css.slice(cursor);
      break;
    }
    const body = css.slice(delimiter.index + 1, closingIndex);
    const kind = importedSvgCssPreludeKind(prelude);
    const nextPrelude = kind === 'selector' ? rewriteSelectorIds(prelude) : prelude;
    const nextBody = kind === 'grouping'
      ? rewriteImportedSvgCssRuleList(body, rewriteSelectorIds)
      : body;
    result += `${nextPrelude}{${nextBody}}`;
    cursor = closingIndex + 1;
  }
  return result;
}

function importedSvgCloneForInstance(source, deep, idRewriter, colorOptions) {
  const clone = source.cloneNode(deep);
  idRewriter?.rewriteTree?.(clone);
  applyImportedSvgColorOverrides(clone, colorOptions);
  return clone;
}

function escapeImportedSvgIdForRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function prefixImportedSvgIds(root, prefix) {
  if (!root) return;
  createImportedSvgIdRewriter(root, prefix).rewriteTree(root);
}

function applyImportedSvgColorOverrides(root, options = {}) {
  if (!root) return;
  const fillColor = normalizeImportedSvgColorOption(options.fillColor);
  const strokeColor = normalizeImportedSvgColorOption(options.strokeColor);
  if (!fillColor && !strokeColor) return;
  [root, ...Array.from(root.querySelectorAll('*'))].forEach(node => {
    if (fillColor) overrideImportedSvgPaintAttribute(node, 'fill', fillColor);
    if (strokeColor) overrideImportedSvgPaintAttribute(node, 'stroke', strokeColor);
    const style = node.getAttribute('style');
    if (!style) return;
    const nextStyle = overrideImportedSvgPaintStyle(
      strokeColor ? overrideImportedSvgPaintStyle(style, 'stroke', strokeColor) : style,
      'fill',
      fillColor
    );
    if (nextStyle !== style) node.setAttribute('style', nextStyle);
  });
}

function normalizeImportedSvgColorOption(value) {
  const color = String(value || '').trim();
  return color ? color : null;
}

function overrideImportedSvgPaintAttribute(node, attribute, color) {
  const value = node.getAttribute(attribute);
  if (!isImportedSvgPaintOverrideable(value)) return;
  node.setAttribute(attribute, color);
}

function overrideImportedSvgPaintStyle(style, property, color) {
  if (!color) return style;
  return String(style || '').replace(
    new RegExp(`(^|;)\\s*${property}\\s*:\\s*([^;]+)`, 'gi'),
    (match, prefix, value) => {
      if (!isImportedSvgPaintOverrideable(value)) return match;
      return `${prefix} ${property}: ${color}`;
    }
  );
}

function isImportedSvgPaintOverrideable(value) {
  const paint = String(value || '').trim();
  if (!paint) return false;
  const lower = paint.toLowerCase();
  if (lower === 'none' || lower === 'transparent' || lower === 'currentcolor' || lower === 'inherit') return false;
  if (lower.startsWith('url(')) return false;
  return true;
}
